import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { mergeDeepWith, flip, or, compose, o, cond, isNil, apply, __, map, T, dissoc, mergeLeft } from 'ramda';
import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { Provider, connect, useDispatch, useSelector } from 'react-redux';
import { DEFAULT_FEATURE, getStateByFeatureAndNamespace, defaultNamespace } from '@redux-syringe/namespaces';
import { alwaysNull } from '@redux-syringe/utils';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _typeof from '@babel/runtime/helpers/typeof';
import { mapProps, getDisplayName } from '@redux-syringe/utils-react';
import hoistNonReactStatics from 'hoist-non-react-statics';
var NamespaceContext = /*#__PURE__*/createContext({
  isUseNamespaceProvided: false,
  namespaces: {},
  useNamespace: alwaysNull
});

// NOTE: We use `flip(or)` so falsy values won't override existing truthy ones.
var mergeContextValues = mergeDeepWith(flip(or));
var NamespaceProvider = function NamespaceProvider(_ref) {
  var children = _ref.children,
    feature = _ref.feature,
    namespace = _ref.namespace,
    store = _ref.store,
    useNamespace = _ref.useNamespace;
  var context = useContext(NamespaceContext);
  var nextContext = useMemo(function () {
    return mergeContextValues(context, {
      namespaces: _defineProperty({}, feature !== null && feature !== void 0 ? feature : DEFAULT_FEATURE, namespace),
      useNamespace: useNamespace,
      isUseNamespaceProvided: Boolean(useNamespace)
    });
  }, [context, feature, namespace, useNamespace]);
  var providerElement = /*#__PURE__*/React.createElement(NamespaceContext.Provider, {
    value: nextContext
  }, children);
  return store ? /*#__PURE__*/React.createElement(Provider, {
    store: store
  }, providerElement) : providerElement;
};
var useNamespace = function useNamespace(feature) {
  var _ref, _namespaces;
  var _useContext = useContext(NamespaceContext),
    _useContext$namespace = _useContext.namespaces,
    namespaces = _useContext$namespace === void 0 ? {} : _useContext$namespace,
    _useContext$useNamesp = _useContext.useNamespace,
    useExternalNamespace = _useContext$useNamesp === void 0 ? alwaysNull : _useContext$useNamesp;
  var externalNamespace = useExternalNamespace(feature !== null && feature !== void 0 ? feature : DEFAULT_FEATURE, namespaces);
  return (_ref = (_namespaces = namespaces[feature !== null && feature !== void 0 ? feature : DEFAULT_FEATURE]) !== null && _namespaces !== void 0 ? _namespaces : externalNamespace) !== null && _ref !== void 0 ? _ref : undefined;
};
var _excluded = ["feature", "namespace"];
var NAMESPACED_CONNECT_PROPS = 'NAMESPACED_CONNECT_PROPS';
var isFunction = function isFunction(value) {
  return typeof value === 'function';
};
var isObject = function isObject(value) {
  return value != null && _typeof(value) === 'object';
};
var alwaysEmptyObject = function alwaysEmptyObject() {
  return {};
};
var wrapMapStateToProps = function wrapMapStateToProps(mapStateToProps) {
  return function (state, ownProps) {
    return mapStateToProps ? mapStateToProps(getStateByFeatureAndNamespace(ownProps[NAMESPACED_CONNECT_PROPS].feature, ownProps[NAMESPACED_CONNECT_PROPS].namespace, state), ownProps, state) : {};
  };
};
var wrapActionCreator = function wrapActionCreator(wrappedDispatch) {
  return function (actionCreator) {
    return compose(wrappedDispatch, actionCreator);
  };
};
var throwTypeError = function throwTypeError() {
  throw new TypeError('mapDispatchToProps is not an object or a function');
};
var wrapMapDispatchToProps = function wrapMapDispatchToProps(mapDispatchToProps) {
  return function (dispatch, ownProps) {
    var wrappedDispatch = o(dispatch, defaultNamespace(ownProps[NAMESPACED_CONNECT_PROPS].namespace));
    return cond([[isNil, alwaysEmptyObject], [isFunction, apply(__, [wrappedDispatch, ownProps])], [isObject, map(wrapActionCreator(wrappedDispatch))], [T, throwTypeError]])(mapDispatchToProps);
  };
};
var rawNamespacedConnect = function rawNamespacedConnect(mapStateToProps, mapDispatchToProps, mergeProps, options) {
  return connect(wrapMapStateToProps(mapStateToProps), wrapMapDispatchToProps(mapDispatchToProps), mergeProps, options);
};
/**
 * @deprecated Prefer `useNamespacedSelector` and `useNamespacedDispatch`.
 */

var namespacedConnect = function namespacedConnect(mapStateToProps, mapDispatchToProps, mergeProps) {
  var _ref = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
    optionFeature = _ref.feature,
    optionNamespace = _ref.namespace,
    options = _objectWithoutProperties(_ref, _excluded);
  return compose(mapProps(function (props) {
    var _ref2, _ref3;
    var propFeature = props.feature,
      propNamespace = props.namespace;
    var feature = (_ref2 = optionFeature !== null && optionFeature !== void 0 ? optionFeature : propFeature) !== null && _ref2 !== void 0 ? _ref2 : DEFAULT_FEATURE;
    var contextNamespace = useNamespace(feature);
    return _objectSpread(_objectSpread({}, props), {}, _defineProperty({}, NAMESPACED_CONNECT_PROPS, {
      feature: feature,
      namespace: (_ref3 = optionNamespace !== null && optionNamespace !== void 0 ? optionNamespace : propNamespace) !== null && _ref3 !== void 0 ? _ref3 : contextNamespace
    }));
  }), rawNamespacedConnect(mapStateToProps, mapDispatchToProps, mergeProps, options), mapProps(dissoc(NAMESPACED_CONNECT_PROPS)));
};
var useNamespacedDispatch = function useNamespacedDispatch() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    optionFeature = _ref.feature,
    optionNamespace = _ref.namespace;
  var dispatch = useDispatch();
  var feature = optionFeature !== null && optionFeature !== void 0 ? optionFeature : DEFAULT_FEATURE;
  var contextNamespace = useNamespace(feature);
  var namespace = optionNamespace !== null && optionNamespace !== void 0 ? optionNamespace : contextNamespace;
  return useCallback(function (action) {
    return dispatch(namespace ? defaultNamespace(namespace, action) : action);
  }, [dispatch, namespace]);
};
var useNamespacedSelector = function useNamespacedSelector(selector, equalityFn) {
  var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
    optionFeature = _ref.feature,
    optionNamespace = _ref.namespace;
  var feature = optionFeature !== null && optionFeature !== void 0 ? optionFeature : DEFAULT_FEATURE;
  var contextNamespace = useNamespace(feature);
  var namespace = optionNamespace !== null && optionNamespace !== void 0 ? optionNamespace : contextNamespace;
  var namespacedSelector = useMemo(function () {
    return o(selector, function (state) {
      return namespace ? getStateByFeatureAndNamespace(feature, namespace, state) : undefined;
    });
  }, [selector, feature, namespace]);
  return useSelector(namespacedSelector, equalityFn);
};
var withNamespaceProvider = function withNamespaceProvider(options) {
  return function (NextComponent) {
    var WithNamespaceProvider = function WithNamespaceProvider(props) {
      return /*#__PURE__*/(
        // NOTE: `NamespaceProvider` will ignore any unknown props.
        React.createElement(NamespaceProvider, mergeLeft(options, props), /*#__PURE__*/React.createElement(NextComponent, props))
      );
    };
    hoistNonReactStatics(WithNamespaceProvider, NextComponent);
    WithNamespaceProvider.displayName = "WithNamespaceProvider(".concat(getDisplayName(NextComponent), ")");
    return WithNamespaceProvider;
  };
};
export { NamespaceContext, NamespaceProvider, namespacedConnect, useNamespace, useNamespacedDispatch, useNamespacedSelector, withNamespaceProvider };